/**
 * PropertyPanel - Side panel for editing selected node properties
 *
 * Features:
 * - Auto-generates form from node's JSON Schema (using RJSF)
 * - Custom color picker widget for colors
 * - Shape type dropdown for shape nodes
 * - Live updates via onChange callback (no submit button)
 * - Closes when no node selected
 *
 * Architecture:
 * - SCHEMAS object defines JSON Schema for each node type
 * - UI_SCHEMAS object defines widget customizations per node type
 * - Changes propagate to parent via onUpdate callback
 * - Parent is responsible for syncing to Yjs (with makeChange wrapper)
 */
import { useMemo, useCallback } from 'react';
import Form from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import type { RJSFSchema, UiSchema, RegistryWidgetsType } from '@rjsf/utils';
import type { Node } from '@xyflow/react';
import { ColorPicker, ColorSwatch } from './ColorPicker';

// ============================================================================
// CUSTOM WIDGETS REGISTRY
// ============================================================================

/**
 * Custom widgets for RJSF forms
 *
 * Register custom widgets here that can be referenced in UI Schema.
 * The key name (e.g., 'ColorWidget') is used in 'ui:widget' property.
 */
const widgets: RegistryWidgetsType = {
  ColorWidget: ColorPicker,
};

// ============================================================================
// JSON SCHEMAS FOR NODE TYPES
// ============================================================================

/**
 * SCHEMAS - JSON Schema definitions for each node type
 *
 * These schemas define:
 * - What properties each node type has
 * - Property types (string, number, enum)
 * - Default values
 * - Validation constraints (min, max)
 *
 * RJSF auto-generates form fields from these schemas.
 */
const SCHEMAS: Record<string, RJSFSchema> = {
  // ---------------------------------------------------------------------------
  // STICKY NOTE SCHEMA
  // Properties: text, color
  // ---------------------------------------------------------------------------
  stickyNote: {
    type: 'object',
    properties: {
      text: {
        type: 'string',
        title: 'Text',
        default: '',
      },
      color: {
        type: 'string',
        title: 'Background Color',
      },
    },
  },

  // ---------------------------------------------------------------------------
  // SHAPE SCHEMA
  // Properties: shapeType, color, strokeColor, strokeWidth
  // ---------------------------------------------------------------------------
  shape: {
    type: 'object',
    properties: {
      shapeType: {
        type: 'string',
        title: 'Shape',
        enum: ['rectangle', 'circle', 'triangle'],
        default: 'rectangle',
      },
      color: {
        type: 'string',
        title: 'Fill Color',
      },
      strokeColor: {
        type: 'string',
        title: 'Border Color',
      },
      strokeWidth: {
        type: 'number',
        title: 'Border Width',
        minimum: 0,
        maximum: 10,
        default: 2,
      },
    },
  },

  // ---------------------------------------------------------------------------
  // PROCESSOR (WORKFLOW) NODE SCHEMA
  // Basic schema - full config comes from NodeDefinition in future
  // ---------------------------------------------------------------------------
  processor: {
    type: 'object',
    properties: {
      label: {
        type: 'string',
        title: 'Label',
      },
    },
  },
};

// ============================================================================
// UI SCHEMAS FOR WIDGET CUSTOMIZATION
// ============================================================================

/**
 * UI_SCHEMAS - Widget customizations for each node type
 *
 * UI Schema controls HOW fields are rendered, not WHAT fields exist.
 * Used to:
 * - Specify custom widgets (e.g., ColorWidget)
 * - Set textarea rows, input types
 * - Configure range sliders, selects
 */
const UI_SCHEMAS: Record<string, UiSchema> = {
  // ---------------------------------------------------------------------------
  // STICKY NOTE UI SCHEMA
  // ---------------------------------------------------------------------------
  stickyNote: {
    text: {
      'ui:widget': 'textarea',  // Multi-line text input
      'ui:options': {
        rows: 4,  // Default height
      },
    },
    color: {
      'ui:widget': 'ColorWidget',  // Use our custom color picker
    },
  },

  // ---------------------------------------------------------------------------
  // SHAPE UI SCHEMA
  // ---------------------------------------------------------------------------
  shape: {
    shapeType: {
      'ui:widget': 'select',  // Dropdown for enum
      // Human-readable labels for enum values
      'ui:enumNames': ['Rectangle', 'Circle', 'Triangle'],
    },
    color: {
      'ui:widget': 'ColorWidget',
    },
    strokeColor: {
      'ui:widget': 'ColorWidget',
    },
    strokeWidth: {
      'ui:widget': 'range',  // Slider for numeric range
    },
  },

  // ---------------------------------------------------------------------------
  // PROCESSOR UI SCHEMA
  // Minimal for now - will expand with NodeDefinition integration
  // ---------------------------------------------------------------------------
  processor: {},
};

// ============================================================================
// PROPERTY PANEL COMPONENT
// ============================================================================

/**
 * PropertyPanelProps - Props for the PropertyPanel component
 */
interface PropertyPanelProps {
  /** Currently selected node (null if none selected) */
  selectedNode: Node | null;

  /**
   * Callback when node data changes
   * Parent should wrap this in makeChange() for Yjs undo tracking
   */
  onUpdate: (nodeId: string, data: Record<string, unknown>) => void;

  /** Callback to close the panel */
  onClose: () => void;
}

/**
 * PropertyPanel - Side panel for editing node properties
 *
 * Renders a form based on the selected node's type.
 * Changes are immediately propagated via onUpdate callback.
 *
 * @param selectedNode - The currently selected React Flow node
 * @param onUpdate - Callback with (nodeId, newData) when form changes
 * @param onClose - Callback to close the panel
 */
export function PropertyPanel({ selectedNode, onUpdate, onClose }: PropertyPanelProps) {
  // ---------------------------------------------------------------------------
  // MEMOIZED SCHEMA SELECTION
  // Re-compute only when node type changes
  // ---------------------------------------------------------------------------

  /**
   * Get JSON Schema for the selected node's type
   * Falls back to processor schema for unknown types
   */
  const schema = useMemo(() => {
    if (!selectedNode) return null;
    return SCHEMAS[selectedNode.type || ''] || SCHEMAS.processor;
  }, [selectedNode?.type]);

  /**
   * Get UI Schema for the selected node's type
   * Falls back to empty object (default widgets)
   */
  const uiSchema = useMemo(() => {
    if (!selectedNode) return {};
    return UI_SCHEMAS[selectedNode.type || ''] || UI_SCHEMAS.processor;
  }, [selectedNode?.type]);

  // ---------------------------------------------------------------------------
  // FORM CHANGE HANDLER
  // ---------------------------------------------------------------------------

  /**
   * Handle form data changes from RJSF
   *
   * RJSF calls this on every field change.
   * We propagate to parent immediately for live updates.
   */
  const handleChange = useCallback(
    ({ formData }: { formData?: Record<string, unknown> }) => {
      if (selectedNode && formData) {
        onUpdate(selectedNode.id, formData);
      }
    },
    [selectedNode, onUpdate]
  );

  // ---------------------------------------------------------------------------
  // EARLY RETURN IF NO NODE SELECTED
  // ---------------------------------------------------------------------------

  // Don't render if no node is selected or no schema exists
  if (!selectedNode || !schema) {
    return null;
  }

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  return (
    <div
      className="w-80 bg-white border-l border-gray-200 flex flex-col"
      style={{ boxShadow: '-2px 0 8px rgba(0,0,0,0.05)' }}
    >
      {/* ===================================================================
          HEADER
          Shows "Properties" title and close button
          =================================================================== */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900">
          Properties
        </h3>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
          aria-label="Close panel"
        >
          {/* X icon */}
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* ===================================================================
          NODE TYPE BADGE
          Shows node type and current color swatch
          =================================================================== */}
      <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
          {/* Human-readable node type names */}
          {selectedNode.type === 'stickyNote' ? 'Sticky Note' :
           selectedNode.type === 'shape' ? 'Shape' :
           selectedNode.type === 'processor' ? 'Node' : String(selectedNode.type || 'Unknown')}
        </span>
        {/* Show color swatch if node has a color property */}
        {typeof selectedNode.data?.color === 'string' && (
          <ColorSwatch color={selectedNode.data.color} size="sm" />
        )}
      </div>

      {/* ===================================================================
          FORM
          RJSF auto-generates form from schema
          =================================================================== */}
      <div className="flex-1 overflow-y-auto p-4">
        <Form
          schema={schema}
          uiSchema={uiSchema}
          formData={selectedNode.data}
          validator={validator}
          widgets={widgets}
          onChange={handleChange}
          // Hide submit button - changes apply immediately
          // Empty fragment as children removes the default submit button
          children={<></>}
          // CSS class for styling
          className="property-panel-form"
        />
      </div>

      {/* ===================================================================
          FOOTER
          Shows truncated node ID for debugging
          =================================================================== */}
      <div className="px-4 py-2 border-t border-gray-200 text-xs text-gray-400">
        ID: {selectedNode.id.slice(0, 8)}...
      </div>
    </div>
  );
}
